/**
 * =============================================================================
 * IDS CRM Web - Engineer bulletins: write or edit a draft
 * =============================================================================
 *
 * Anyone signed in writes a draft (Gareth 2026-09-11..13). Saving never
 * publishes: an engineer then sends it for review, a reviewer publishes it.
 *
 *  - Fault codes go to the API as the raw string typed. The chips underneath
 *    show what will be stored, through the same parser the API uses.
 *  - The groups ticked are who is TOLD when it is published, not who may read
 *    it. Anyone signed in can browse published bulletins.
 *  - Only a draft can be edited. A published bulletin is replaced by a newer
 *    one ("replace with a newer one").
 *  - On edit, attachments is the whole list to keep: the files already on the
 *    bulletin by attachment_id, and the new ones with their data.
 * =============================================================================
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import { useAuth } from '../context/AuthContext';
import { FaultCodeChips } from '../components/BulletinParts';
import { filesToAttachmentPayload } from '../utils/attachments';
import { formatFaultCodes } from '../utils/faultCodes';
import {
  BODY_MAX_LENGTH,
  MAX_BULLETIN_ATTACHMENTS,
  NEW_FILES_BUDGET_BYTES,
  TITLE_MAX_LENGTH,
  formatFileSize,
  isBulletinReviewer,
  newAttachmentBytes
} from '../utils/bulletins';
import './Bulletins.css';

export function tooMuchToSaveMessage(bytes) {
  return `The new files add up to ${formatFileSize(bytes)}, more than the ${formatFileSize(NEW_FILES_BUDGET_BYTES)} `
    + 'that can be saved in one go. Remove some, save the draft, then edit it to add the rest.';
}

/**
 * The most one file can be. It is the one-save budget, not the 8 MB the
 * picker's own check allows: a single file over the budget is refused in
 * every save, so "save, then add the rest" could never get it saved.
 */
export const FILE_LIMIT_LABEL = `${Math.floor(NEW_FILES_BUDGET_BYTES / (1024 * 1024))} MB`;

export function fileTooBigMessage(name) {
  return `'${name}' is too big. Each file can be up to ${FILE_LIMIT_LABEL}.`;
}

/**
 * /bulletins/new and /bulletins/:id/edit render this same component, and
 * react-router keeps the instance when moving between them. Keyed by id, so
 * "Write a bulletin" from the "can't be edited" panel gets a fresh, empty
 * form instead of the panel it was pressed on, with its id gone.
 */
function BulletinForm() {
  const { id } = useParams();
  return <BulletinEditor key={id || 'new'} id={id} />;
}

function BulletinEditor({ id }) {
  const editing = Boolean(id);
  const navigate = useNavigate();
  const { authFetch, user } = useAuth();
  const reviewer = isBulletinReviewer(user);

  const [loading, setLoading] = useState(editing);
  const [missing, setMissing] = useState(false);
  const [notDraft, setNotDraft] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [faultCodes, setFaultCodes] = useState('');
  const [groupIds, setGroupIds] = useState([]);
  const [keptFiles, setKeptFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);

  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groupsError, setGroupsError] = useState('');

  const [fileError, setFileError] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const fileKey = useRef(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await authFetch('/api/v1/groups');
        const result = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (!response.ok) {
          setGroupsError(result.message || 'Could not load the groups.');
          return;
        }
        setGroups(Array.isArray(result.data) ? result.data : []);
      } catch (err) {
        if (!cancelled) setGroupsError('Could not load the groups. Check your connection.');
      } finally {
        if (!cancelled) setGroupsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch]);

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await authFetch(`/api/v1/bulletins/${id}`);
        const result = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (response.status === 404) {
          setMissing(true);
          return;
        }
        if (!response.ok) {
          setLoadError(result.message || 'Could not load this bulletin.');
          return;
        }
        const bulletin = result.data || {};
        if (bulletin.status !== 'draft') {
          setNotDraft(true);
          return;
        }
        setTitle(bulletin.title || '');
        setBody(bulletin.body || '');
        setFaultCodes(formatFaultCodes(bulletin.fault_codes || []));
        setGroupIds((bulletin.groups || []).map(group => group.group_id));
        setKeptFiles(Array.isArray(bulletin.attachments) ? bulletin.attachments : []);
      } catch (err) {
        if (!cancelled) setLoadError('Could not load this bulletin. Check your connection and try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch, id]);

  const toggleGroup = (groupId) => {
    setGroupIds(prev => (prev.includes(groupId)
      ? prev.filter(entry => entry !== groupId)
      : [...prev, groupId]));
  };

  const addFiles = async (event) => {
    const input = event.target;
    const picked = Array.from(input.files || []);
    setFileError('');
    if (picked.length === 0) return;
    // Refused on picking, not at Save: a file bigger than one save can carry
    // would be refused on every save. The others picked with it still go on.
    const tooBig = picked.filter(file => file.size > NEW_FILES_BUDGET_BYTES);
    const fits = picked.filter(file => !(file.size > NEW_FILES_BUDGET_BYTES));
    const problems = tooBig.map(file => fileTooBigMessage(file.name));
    try {
      if (fits.length > 0) {
        if (keptFiles.length + newFiles.length + fits.length > MAX_BULLETIN_ATTACHMENTS) {
          throw new Error(`A bulletin can hold ${MAX_BULLETIN_ATTACHMENTS} files at most. Remove one before adding more.`);
        }
        const payload = await filesToAttachmentPayload(fits);
        setNewFiles(prev => [
          ...prev,
          ...payload.map(file => ({ ...file, key: `new-${fileKey.current++}` }))
        ]);
      }
    } catch (err) {
      problems.push(err.message);
    } finally {
      // Cleared, so picking the same photo again after removing it still fires.
      input.value = '';
    }
    if (problems.length > 0) setFileError(problems.join(' '));
  };

  const save = async (event) => {
    event.preventDefault();
    setError('');
    if (!title.trim()) {
      setError('Give the bulletin a title.');
      return;
    }
    if (!body.trim()) {
      setError('Write the bulletin text.');
      return;
    }
    const newBytes = newAttachmentBytes(newFiles);
    if (newBytes > NEW_FILES_BUDGET_BYTES) {
      setError(tooMuchToSaveMessage(newBytes));
      return;
    }

    const attachments = [
      ...(editing ? keptFiles.map(file => ({ attachment_id: file.attachment_id })) : []),
      ...newFiles.map(file => ({ name: file.name, dataUrl: file.dataUrl }))
    ];

    setSaving(true);
    try {
      const response = await authFetch(editing ? `/api/v1/bulletins/${id}` : '/api/v1/bulletins', {
        method: editing ? 'PUT' : 'POST',
        body: JSON.stringify({
          title: title.trim(),
          body,
          fault_codes: faultCodes,
          group_ids: groupIds,
          attachments
        })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(response.status === 413
          ? tooMuchToSaveMessage(newBytes)
          : (result.message || 'Could not save the draft. Try again.'));
        setSaving(false);
        return;
      }
      const savedId = (result.data && result.data.bulletin_id) || id;
      // Replace, so Back from the saved bulletin goes to where the form was
      // opened from. Pushed, Back showed an empty "Write a bulletin" form (it
      // looked as if the save was lost) or, once published, the "can't be
      // edited" panel.
      navigate(`/bulletins/${savedId}`, { replace: true });
    } catch (err) {
      setError('Could not save the draft. Check your connection and try again.');
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="bulletin-page"><div className="bulletin-loading">Loading…</div></div>;
  }

  if (missing) {
    return (
      <div className="bulletin-page">
        <div className="bulletin-panel">
          <h1 className="bulletin-panel-title">This bulletin doesn't exist or you can't see it</h1>
          <BackToList to="/bulletins" className="btn-secondary bulletin-button">Back to bulletins</BackToList>
        </div>
      </div>
    );
  }

  if (notDraft) {
    return (
      <div className="bulletin-page">
        <div className="bulletin-panel">
          <p className="bulletin-panel-title">A published bulletin can't be edited. Write a newer one to replace it.</p>
          <div className="bulletin-actions">
            <Link to={`/bulletins/${id}`} className="btn-secondary bulletin-button">Back to the bulletin</Link>
            <Link to="/bulletins/new" className="btn-primary bulletin-button">Write a bulletin</Link>
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bulletin-page">
        <BackToList to="/bulletins" className="bulletin-back">← Bulletins</BackToList>
        <div className="bulletin-error" role="alert">{loadError}</div>
      </div>
    );
  }

  const newBytes = newAttachmentBytes(newFiles);
  const fileCount = keptFiles.length + newFiles.length;

  return (
    <div className="bulletin-page bulletin-form-page">
      {editing ? (
        <Link to={`/bulletins/${id}`} className="bulletin-back">← Back to the bulletin</Link>
      ) : (
        <BackToList to="/bulletins" className="bulletin-back">← Bulletins</BackToList>
      )}

      <div className="bulletin-header">
        <div>
          <h1>{editing ? 'Edit draft bulletin' : 'Write a bulletin'}</h1>
          <p>
            {reviewer
              ? 'Saved as a draft. You can publish it from the bulletin once it is saved.'
              : 'Saved as a draft. When it is ready, send it for review and a team leader or manager will publish it.'}
          </p>
        </div>
      </div>

      <form className="bulletin-form" onSubmit={save} noValidate>
        <div className="bulletin-field">
          <label htmlFor="bulletin-title">Title</label>
          <input
            id="bulletin-title"
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            maxLength={TITLE_MAX_LENGTH}
            placeholder="e.g. Teat cup not attaching after E101"
            required
          />
        </div>

        <div className="bulletin-field">
          <label htmlFor="bulletin-body">Details</label>
          <textarea
            id="bulletin-body"
            value={body}
            onChange={e => setBody(e.target.value)}
            maxLength={BODY_MAX_LENGTH}
            rows={12}
            placeholder="What the fault looks like, what fixed it, and anything to check first."
            required
          />
          <span className="bulletin-hint">
            Plain text. {body.length.toLocaleString('en-GB')} of {BODY_MAX_LENGTH.toLocaleString('en-GB')} characters.
          </span>
        </div>

        <div className="bulletin-field">
          <label htmlFor="bulletin-codes-input">Fault codes</label>
          <input
            id="bulletin-codes-input"
            type="text"
            value={faultCodes}
            onChange={e => setFaultCodes(e.target.value)}
            placeholder="e.g. E101, E205"
            autoComplete="off"
            autoCapitalize="characters"
          />
          <FaultCodeChips codes={faultCodes} />
          <span className="bulletin-hint">
            Optional. Separate codes with commas. Engineers searching for a code will find this bulletin.
          </span>
        </div>

        <fieldset className="bulletin-fieldset">
          <legend>Groups to tell</legend>
          <p className="bulletin-hint">
            Members of the groups you tick are told when it is published. Anyone signed in can browse
            published bulletins, whether they are in a group or not.
          </p>
          {groupsLoading ? (
            <p className="bulletin-hint">Loading groups…</p>
          ) : groupsError ? (
            <p className="bulletin-error">{groupsError}</p>
          ) : groups.length === 0 ? (
            <p className="bulletin-warning">
              No groups have been set up yet, so nobody will be told when this is published. It can
              still be published for people to browse.
            </p>
          ) : (
            <ul className="bulletin-choice-list">
              {groups.map(group => (
                <li key={group.group_id}>
                  <label className="bulletin-choice">
                    <input
                      type="checkbox"
                      checked={groupIds.includes(group.group_id)}
                      onChange={() => toggleGroup(group.group_id)}
                    />
                    <span className="bulletin-choice-text">
                      <span className="bulletin-choice-name">{group.name}</span>
                      <span className="bulletin-choice-meta">
                        {Number.isFinite(Number(group.member_count))
                          ? `${group.member_count} member${Number(group.member_count) === 1 ? '' : 's'}`
                          : ''}
                        {group.description ? `${Number.isFinite(Number(group.member_count)) ? ' · ' : ''}${group.description}` : ''}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </fieldset>

        <fieldset className="bulletin-fieldset">
          <legend>Photos and files</legend>
          <p className="bulletin-hint">
            Photos, PDFs or text files, up to {FILE_LIMIT_LABEL} each and {MAX_BULLETIN_ATTACHMENTS} in total.
          </p>
          {fileCount > 0 && (
            <ul className="bulletin-file-list">
              {keptFiles.map(file => (
                <li key={file.attachment_id} className="bulletin-file">
                  <span className="bulletin-file-name">{file.name}</span>
                  {formatFileSize(file.size) && <span className="bulletin-file-size">{formatFileSize(file.size)}</span>}
                  <button
                    type="button"
                    className="btn-secondary bulletin-button"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => setKeptFiles(prev => prev.filter(entry => entry.attachment_id !== file.attachment_id))}
                  >
                    Remove
                  </button>
                </li>
              ))}
              {newFiles.map(file => (
                <li key={file.key} className="bulletin-file">
                  <span className="bulletin-file-name">{file.name}</span>
                  <span className="bulletin-file-size">
                    {formatFileSize(file.size)}{formatFileSize(file.size) ? ' · ' : ''}not saved yet
                  </span>
                  <button
                    type="button"
                    className="btn-secondary bulletin-button"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => setNewFiles(prev => prev.filter(entry => entry.key !== file.key))}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
          {newBytes > NEW_FILES_BUDGET_BYTES && (
            <p className="bulletin-warning">{tooMuchToSaveMessage(newBytes)}</p>
          )}
          {fileError && <p className="bulletin-error" role="alert">{fileError}</p>}
          {fileCount < MAX_BULLETIN_ATTACHMENTS && (
            <label className="btn-secondary bulletin-button bulletin-file-pick">
              Add files
              <input
                type="file"
                multiple
                accept="image/*,application/pdf,text/plain"
                className="bulletin-file-input"
                onChange={addFiles}
              />
            </label>
          )}
        </fieldset>

        {error && <div className="bulletin-error" role="alert">{error}</div>}

        <div className="form-actions bulletin-actions">
          <button type="submit" className="btn-primary bulletin-button" disabled={saving}>
            {saving ? 'Saving…' : 'Save draft'}
          </button>
          {editing ? (
            <Link to={`/bulletins/${id}`} className="btn-secondary bulletin-button">Cancel</Link>
          ) : (
            <BackToList to="/bulletins" className="btn-secondary bulletin-button">Cancel</BackToList>
          )}
        </div>
      </form>
    </div>
  );
}

export default BulletinForm;
